import { useState } from 'react';
import { Button, Empty, Select, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetAllShootTypes } from '../../hooks/useCompanies';
import { useCsvKeys, useCsvValues } from '../../hooks/useReports';

const KIND_CSV = 'csvData';
const KIND_ORDER_NAME = 'article.orderName';
const KIND_MODEL_NAME = 'model.name';
const KIND_SHOOT = 'shootTypes';

const KIND_OPTIONS = [
    { value: KIND_CSV, label: 'csvData field' },
    { value: KIND_ORDER_NAME, label: 'Order name contains' },
    { value: KIND_MODEL_NAME, label: 'Model name' },
    { value: KIND_SHOOT, label: 'Shoot types' },
];

const TEXT_OP_OPTIONS = [
    { value: 'contains', label: 'Includes', hint: 'Model name contains any of these strings.' },
    { value: 'excludes', label: 'Excludes', hint: 'Model name does not contain any of these strings.' },
];

const SHOOT_OP_OPTIONS = [
    { value: 'all', label: 'Has all of these', hint: 'Every selected type is on the SKU. Extra types are OK.' },
    { value: 'any', label: 'Has at least one', hint: 'SKU has one or more of the selected types.' },
    { value: 'exact', label: 'Has exactly these', hint: 'SKU types match the selection. Nothing extra, nothing missing.' },
    { value: 'only', label: 'Has only from this list', hint: 'No extra types. SKU may have a subset of the selection.' },
    { value: 'none', label: 'Has none of these', hint: 'SKU has none of the selected types.' },
];

function fieldKey(field) {
    return String(field || '').replace(/^csvData\./, '');
}

function filterValues(filter) {
    if (Array.isArray(filter?.value)) return filter.value.map(String);
    if (filter?.value == null || filter.value === '') return [];
    return [String(filter.value)];
}

function filterLabel(filter) {
    if (filter.field === KIND_ORDER_NAME) return 'Order name';
    if (filter.field === KIND_MODEL_NAME) return 'Model name';
    if (filter.field === KIND_SHOOT) return 'Shoot types';
    return fieldKey(filter.field);
}

function filterHint(filter) {
    const values = filterValues(filter);
    if (filter.op === 'contains') return `includes ${values.join(', ') || '—'}`;
    if (filter.op === 'excludes') return `excludes ${values.join(', ') || '—'}`;
    const shoot = SHOOT_OP_OPTIONS.find(item => item.value === filter.op);
    if (filter.field === KIND_SHOOT && shoot) return `${shoot.label.toLowerCase()}: ${values.join(', ') || '—'}`;
    return values.join(', ') || '—';
}

function filterRowKey(filter) {
    return `${filter.field}:${filter.op || ''}`;
}

function CsvValueSelect({ companyCode, csvKey, value, onChange }) {
    const { data, isFetching } = useCsvValues(companyCode, csvKey, !!csvKey);
    const sampled = data || [];
    const extras = (value || []).filter(item => !sampled.includes(item));
    const options = [...extras, ...sampled].map(item => ({ value: item, label: item }));

    return (
        <Select
            mode="multiple"
            allowClear
            showSearch
            loading={isFetching}
            placeholder={csvKey ? 'Select values' : 'Pick a field first'}
            value={value}
            options={options}
            onChange={onChange}
            style={{ minWidth: 220, flex: 1 }}
            maxTagCount="responsive"
        />
    );
}

export default function CsvDataFilterBuilder({ companyCode, filters, onChange }) {
    const { data: keys, isFetching } = useCsvKeys(companyCode, !!companyCode);
    const shootTypesQuery = useGetAllShootTypes();
    const shootTypeNames = Array.isArray(shootTypesQuery.data?.data)
        ? shootTypesQuery.data.data
        : Array.isArray(shootTypesQuery.data)
            ? shootTypesQuery.data
            : [];
    const [kind, setKind] = useState(KIND_CSV);
    const [csvKey, setCsvKey] = useState(undefined);
    const [shootOp, setShootOp] = useState('all');
    const [textOp, setTextOp] = useState('contains');
    const [values, setValues] = useState([]);
    const usedCsv = new Set(
        (filters || []).filter(filter => String(filter.field || '').startsWith('csvData.')).map(filter => fieldKey(filter.field))
    );
    const shootHint = SHOOT_OP_OPTIONS.find(item => item.value === shootOp)?.hint;
    const textHint = TEXT_OP_OPTIONS.find(item => item.value === textOp)?.hint;
    const canAdd =
        kind === KIND_ORDER_NAME || kind === KIND_SHOOT || kind === KIND_MODEL_NAME
            ? values.length > 0
            : !!csvKey && values.length > 0;

    const addFilter = () => {
        if (kind === KIND_ORDER_NAME) {
            if (!values.length) return;
            const next = (filters || []).filter(filter => filter.field !== KIND_ORDER_NAME);
            onChange([...next, { field: KIND_ORDER_NAME, op: 'contains', value: values }]);
            setValues([]);
            return;
        }
        if (kind === KIND_MODEL_NAME) {
            if (!values.length) return;
            const next = (filters || []).filter(filter => !(filter.field === KIND_MODEL_NAME && filter.op === textOp));
            onChange([...next, { field: KIND_MODEL_NAME, op: textOp, value: values }]);
            setValues([]);
            return;
        }
        if (kind === KIND_SHOOT) {
            if (!values.length) return;
            const next = (filters || []).filter(filter => !(filter.field === KIND_SHOOT && filter.op === shootOp));
            onChange([...next, { field: KIND_SHOOT, op: shootOp, value: values }]);
            setValues([]);
            return;
        }
        if (!csvKey || !values.length) return;
        const next = (filters || []).filter(filter => fieldKey(filter.field) !== csvKey);
        onChange([...next, { field: `csvData.${csvKey}`, op: 'in', value: values }]);
        setCsvKey(undefined);
        setValues([]);
    };

    const onKindChange = nextKind => {
        setKind(nextKind);
        setCsvKey(undefined);
        setValues([]);
        setShootOp('all');
        setTextOp('contains');
    };

    return (
        <div className="reports-filter-builder">
            <div className="reports-filter-add">
                <Select value={kind} options={KIND_OPTIONS} onChange={onKindChange} style={{ minWidth: 180 }} />
                {kind === KIND_CSV ? (
                    <>
                        <Select
                            showSearch
                            allowClear
                            loading={isFetching}
                            placeholder="csvData field"
                            value={csvKey}
                            options={(keys || [])
                                .filter(key => !usedCsv.has(key) || key === csvKey)
                                .map(key => ({ value: key, label: key }))}
                            onChange={key => {
                                setCsvKey(key);
                                setValues([]);
                            }}
                            style={{ minWidth: 200 }}
                        />
                        <CsvValueSelect companyCode={companyCode} csvKey={csvKey} value={values} onChange={setValues} />
                    </>
                ) : kind === KIND_ORDER_NAME ? (
                    <Select
                        mode="tags"
                        tokenSeparators={[',']}
                        allowClear
                        placeholder="Type keywords, press Enter"
                        value={values}
                        onChange={setValues}
                        style={{ minWidth: 260, flex: 1 }}
                    />
                ) : kind === KIND_MODEL_NAME ? (
                    <>
                        <Select
                            value={textOp}
                            options={TEXT_OP_OPTIONS.map(item => ({ value: item.value, label: item.label }))}
                            onChange={setTextOp}
                            style={{ minWidth: 140 }}
                        />
                        <Select
                            mode="tags"
                            tokenSeparators={[',']}
                            allowClear
                            placeholder="Type a string, press Enter"
                            value={values}
                            onChange={setValues}
                            style={{ minWidth: 260, flex: 1 }}
                        />
                    </>
                ) : (
                    <>
                        <Select
                            value={shootOp}
                            options={SHOOT_OP_OPTIONS.map(item => ({ value: item.value, label: item.label }))}
                            onChange={setShootOp}
                            style={{ minWidth: 200 }}
                        />
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            loading={shootTypesQuery.isFetching}
                            placeholder="Select shoot types"
                            value={values}
                            options={shootTypeNames.map(name => ({ value: name, label: name }))}
                            onChange={setValues}
                            style={{ minWidth: 240, flex: 1 }}
                            maxTagCount="responsive"
                        />
                    </>
                )}
                <Button type="primary" icon={<PlusOutlined />} onClick={addFilter} disabled={!canAdd}>
                    Add filter
                </Button>
            </div>
            {kind === KIND_SHOOT && shootHint ? <p className="reports-filter-hint">{shootHint}</p> : null}
            {kind === KIND_MODEL_NAME && textHint ? <p className="reports-filter-hint">{textHint}</p> : null}
            {isFetching && !keys?.length && kind === KIND_CSV ? (
                <Spin />
            ) : (filters || []).length ? (
                <ul className="reports-filter-rows">
                    {(filters || []).map(filter => (
                        <li key={filterRowKey(filter)}>
                            <div>
                                <strong>{filterLabel(filter)}</strong>
                                <em>{filterHint(filter)}</em>
                            </div>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                    onChange(
                                        (filters || []).filter(
                                            item => !(item.field === filter.field && item.op === filter.op)
                                        )
                                    )
                                }
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No filters on this project type" />
            )}
        </div>
    );
}
